import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useRefreshListener } from '../hooks/useRefreshListener';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Avatar, AvatarFallback,
} from '../components/ui/avatar';
import {
  CheckCircle2,
  CalendarClock,
  AlertTriangle,
  Loader2,
  ChevronLeft,
  Search,
  TicketIcon,
  ArrowRight,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return format(parseISO(iso), "dd MMM yyyy", { locale: pt }); } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return format(parseISO(iso), "dd MMM yyyy 'às' HH:mm", { locale: pt }); } catch { return iso; }
};

const initials = (n) => (n || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

const ACTION_LABEL = {
  created: 'Criado',
  verified: 'Marcado como Verificado',
  rescheduled: 'Reagendado',
  reported_problem: 'Problema Reportado',
};

export const FollowUpsFaturaPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ follow_ups: [], counters: {}, now: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('pendente');

  // Detail panel state
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Action dialogs
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyNotes, setVerifyNotes] = useState('');

  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedDays, setReschedDays] = useState('14');
  const [reschedNotes, setReschedNotes] = useState('');

  const [problemOpen, setProblemOpen] = useState(false);
  const [problemDesc, setProblemDesc] = useState('');
  const [problemPrio, setProblemPrio] = useState('normal');

  const [acting, setActing] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/follow-ups`, { params: { status: filterStatus } });
      setData(res.data);
    } catch {
      toast.error('Erro ao carregar follow-ups');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  const fetchDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/follow-ups/${id}`);
      setDetail(res.data);
    } catch {
      toast.error('Erro ao carregar detalhe');
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { if (selectedId) fetchDetail(selectedId); }, [selectedId, fetchDetail]);
  useRefreshListener(() => { fetchList(); if (selectedId) fetchDetail(selectedId); });

  const filtered = (data.follow_ups || []).filter(f => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (f.client_name || '').toLowerCase().includes(s) ||
      (f.contract_codigo || '').toString().includes(s) ||
      (f.assigned_to_name || '').toLowerCase().includes(s)
    );
  });

  const handleVerify = async () => {
    if (verifyNotes.trim().length < 10) {
      toast.error('As notas são obrigatórias (mínimo 10 caracteres)');
      return;
    }
    setActing(true);
    try {
      await axios.post(`${API_URL}/follow-ups/${selectedId}/verify`, { notes: verifyNotes.trim() });
      toast.success('Follow-up verificado');
      setVerifyOpen(false); setVerifyNotes('');
      fetchList();
      fetchDetail(selectedId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro');
    } finally {
      setActing(false);
    }
  };

  const handleReschedule = async () => {
    const days = parseInt(reschedDays, 10);
    if (!days || days <= 0) { toast.error('Indica nº de dias válido'); return; }
    if (reschedNotes.trim().length < 10) {
      toast.error('O motivo é obrigatório (mínimo 10 caracteres)');
      return;
    }
    setActing(true);
    try {
      await axios.post(`${API_URL}/follow-ups/${selectedId}/reschedule`, { days, notes: reschedNotes.trim() });
      toast.success(`Reagendado para +${days} dias`);
      setReschedOpen(false); setReschedNotes(''); setReschedDays('14');
      fetchList();
      fetchDetail(selectedId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro');
    } finally {
      setActing(false);
    }
  };

  const handleReportProblem = async () => {
    if (problemDesc.trim().length < 10) {
      toast.error('A descrição é obrigatória (mínimo 10 caracteres)');
      return;
    }
    setActing(true);
    try {
      const res = await axios.post(`${API_URL}/follow-ups/${selectedId}/report-problem`, {
        description: problemDesc.trim(),
        priority: problemPrio,
      });
      toast.success('Ticket criado');
      setProblemOpen(false); setProblemDesc(''); setProblemPrio('normal');
      fetchList();
      fetchDetail(selectedId);
      setTimeout(() => navigate(`/tickets/${res.data.ticket_id}`), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro');
    } finally {
      setActing(false);
    }
  };

  const counters = data.counters || {};

  return (
    <Layout>
      <div className="space-y-6" data-testid="followups-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-[#2D3E6F]" />
            Follow-ups 1ª Fatura
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Verifica se os clientes ativados receberam corretamente a primeira fatura. Disparo aos 45 dias após ativação.
          </p>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Total</p>
              <p className="text-2xl font-bold text-slate-700" data-testid="counter-total">{counters.total || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl bg-amber-50/40">
            <CardContent className="p-4">
              <p className="text-xs text-amber-700">A verificar agora</p>
              <p className="text-2xl font-bold text-amber-700" data-testid="counter-due">{counters.due || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl bg-rose-50/40">
            <CardContent className="p-4">
              <p className="text-xs text-rose-700">Atrasado (90+ dias)</p>
              <p className="text-2xl font-bold text-rose-700" data-testid="counter-red">{counters.overdue_red || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl bg-slate-50/60">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Próximos</p>
              <p className="text-2xl font-bold text-slate-700" data-testid="counter-upcoming">{counters.upcoming || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs text-slate-500 mb-1 block">Pesquisar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cliente, código, comercial..."
                  className="h-9 pl-9"
                  data-testid="followup-search"
                />
              </div>
            </div>
            <div className="min-w-[160px]">
              <Label className="text-xs text-slate-500 mb-1 block">Estado</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendente">Pendentes</SelectItem>
                  <SelectItem value="verificado">Verificados</SelectItem>
                  <SelectItem value="anulado">Anulados</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="rounded-2xl border-slate-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : filtered.length === 0 ? (
            <CardContent className="text-center py-16">
              <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
              <p className="text-slate-400">Sem follow-ups que correspondam.</p>
            </CardContent>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead>Contrato</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Comercial</TableHead>
                    <TableHead>Ativação</TableHead>
                    <TableHead>Prazo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((f, idx) => {
                    const sched = f.scheduled_at ? new Date(f.scheduled_at) : null;
                    const now = new Date();
                    const isRed = f.is_red || (sched && (now - sched) / (1000 * 60 * 60 * 24) >= 45);
                    const isDue = f.is_due || (sched && sched <= now && !isRed);
                    return (
                      <TableRow
                        key={f.id}
                        className={`cursor-pointer hover:bg-slate-50/70 ${isRed ? 'bg-rose-50/40' : isDue ? 'bg-amber-50/30' : ''}`}
                        onClick={() => setSelectedId(f.id)}
                        data-testid={`followup-row-${idx}`}
                      >
                        <TableCell className="font-mono text-xs">
                          <Link
                            to={`/contracts/${f.contract_category}/${f.contract_company}/${f.contract_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-[#2D3E6F] hover:underline"
                          >
                            #{f.contract_codigo || f.contract_id?.slice(0, 6)}
                          </Link>
                        </TableCell>
                        <TableCell className="font-medium text-slate-800">{f.client_name}</TableCell>
                        <TableCell className="text-sm text-slate-600">{f.assigned_to_name || '—'}</TableCell>
                        <TableCell className="text-xs text-slate-500">{fmtDate(f.activated_at)}</TableCell>
                        <TableCell className={`text-sm ${isRed ? 'font-bold text-rose-700' : isDue ? 'font-semibold text-amber-700' : 'text-slate-600'}`}>
                          {fmtDate(f.scheduled_at)}
                          {isRed && <AlertTriangle className="w-3.5 h-3.5 inline ml-1 -mt-0.5 text-rose-600" />}
                        </TableCell>
                        <TableCell>
                          {f.status === 'pendente' && isRed && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200">Atrasado</span>}
                          {f.status === 'pendente' && isDue && !isRed && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">A verificar</span>}
                          {f.status === 'pendente' && !isDue && !isRed && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">Próximo</span>}
                          {f.status === 'verificado' && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">Verificado</span>}
                          {f.status === 'anulado' && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Anulado</span>}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" data-testid={`open-detail-${idx}`}>
                            Abrir <ArrowRight className="w-3.5 h-3.5 ml-1" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* Detail dialog */}
      <Dialog open={Boolean(selectedId)} onOpenChange={(o) => { if (!o) { setSelectedId(null); setDetail(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="followup-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-[#2D3E6F]" />
              Follow-up 1ª Fatura
            </DialogTitle>
          </DialogHeader>
          {detailLoading || !detail ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-slate-400">Cliente</p>
                  <p className="font-medium text-slate-800">{detail.follow_up.client_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Telefone</p>
                  {detail.follow_up.client_telefones && detail.follow_up.client_telefones.length > 0 ? (
                    <div className="space-y-0.5" data-testid="followup-phones">
                      {detail.follow_up.client_telefones.map((t, i) => (
                        <p key={i} className="font-medium text-slate-800 text-sm">
                          {t.numero}
                          {t.tipo && t.tipo !== 'principal' && <span className="text-xs text-slate-400 ml-1">({t.tipo})</span>}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="font-medium text-slate-400">—</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-slate-400">Comercial</p>
                  <p className="font-medium text-slate-800">{detail.follow_up.assigned_to_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Contrato</p>
                  <Link
                    to={`/contracts/${detail.follow_up.contract_category}/${detail.follow_up.contract_company}/${detail.follow_up.contract_id}`}
                    className="font-mono text-[#2D3E6F] hover:underline"
                  >
                    #{detail.follow_up.contract_codigo}
                  </Link>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Ativação</p>
                  <p className="font-medium text-slate-800">{fmtDate(detail.follow_up.activated_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Prazo de verificação</p>
                  <p className="font-medium text-slate-800">{fmtDate(detail.follow_up.scheduled_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Estado</p>
                  <p className="font-medium text-slate-800 capitalize">{detail.follow_up.status}</p>
                </div>
              </div>

              {detail.follow_up.status === 'pendente' && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                  <Button
                    onClick={() => setVerifyOpen(true)}
                    className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                    data-testid="followup-verify-btn"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Marcar Verificado
                  </Button>
                  <Button
                    onClick={() => setReschedOpen(true)}
                    variant="outline"
                    className="gap-2"
                    data-testid="followup-reschedule-btn"
                  >
                    <CalendarClock className="w-4 h-4" /> Reagendar
                  </Button>
                  <Button
                    onClick={() => setProblemOpen(true)}
                    variant="outline"
                    className="gap-2 border-rose-300 text-rose-700 hover:bg-rose-50"
                    data-testid="followup-report-btn"
                  >
                    <AlertTriangle className="w-4 h-4" /> Reportar Problema
                  </Button>
                </div>
              )}

              {/* Thread */}
              <div className="pt-2">
                <p className="text-sm font-semibold text-slate-700 mb-3">Histórico ({(detail.actions || []).length})</p>
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1" data-testid="followup-thread">
                  {(!detail.actions || detail.actions.length === 0) && (
                    <p className="text-xs text-slate-400 text-center py-4">Sem interações ainda.</p>
                  )}
                  {(detail.actions || []).map(a => (
                    <div key={a.id} className="flex gap-3" data-testid={`action-${a.id}`}>
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarFallback className="bg-[#2D3E6F] text-white text-xs">{initials(a.created_by_name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-slate-800">{a.created_by_name}</span>
                          <span className="text-xs text-slate-400">{fmtDateTime(a.created_at)}</span>
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                            {ACTION_LABEL[a.type] || a.type}
                          </span>
                        </div>
                        {a.notes && <p className="text-sm text-slate-700 whitespace-pre-wrap mt-0.5">{a.notes}</p>}
                        {a.meta?.days && (
                          <p className="text-xs text-slate-500 mt-0.5">+{a.meta.days} dias → {fmtDate(a.meta.new_scheduled_at)}</p>
                        )}
                        {a.meta?.ticket_id && (
                          <Link to={`/tickets/${a.meta.ticket_id}`} className="text-xs text-[#2D3E6F] hover:underline inline-flex items-center gap-1 mt-0.5">
                            <TicketIcon className="w-3 h-3" /> Ticket criado
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Verify dialog */}
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Marcar como Verificado</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Confirmas que o cliente recebeu corretamente a 1ª fatura?</p>
            <div>
              <Label>Notas <span className="text-rose-500">*</span></Label>
              <Textarea
                value={verifyNotes}
                onChange={(e) => setVerifyNotes(e.target.value)}
                rows={3}
                placeholder="Ex: cliente confirmou via WhatsApp em 12/05 que recebeu a fatura, valor correcto"
                data-testid="verify-notes-input"
              />
              <p className="text-xs text-slate-400 mt-1">Mínimo 10 caracteres ({verifyNotes.trim().length}/10)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyOpen(false)} disabled={acting}>Cancelar</Button>
            <Button onClick={handleVerify} disabled={acting || verifyNotes.trim().length < 10} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="confirm-verify">
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog */}
      <Dialog open={reschedOpen} onOpenChange={setReschedOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reagendar Follow-up</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Adiar para... (a partir de hoje)</Label>
              <Select value={reschedDays} onValueChange={setReschedDays}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">+7 dias</SelectItem>
                  <SelectItem value="14">+14 dias</SelectItem>
                  <SelectItem value="30">+30 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Motivo <span className="text-rose-500">*</span></Label>
              <Textarea
                value={reschedNotes}
                onChange={(e) => setReschedNotes(e.target.value)}
                rows={3}
                placeholder="Cliente em férias até 1 jun, fatura ainda não emitida pela EDP..."
                data-testid="reschedule-notes-input"
              />
              <p className="text-xs text-slate-400 mt-1">Mínimo 10 caracteres ({reschedNotes.trim().length}/10)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReschedOpen(false)} disabled={acting}>Cancelar</Button>
            <Button onClick={handleReschedule} disabled={acting || reschedNotes.trim().length < 10} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2" data-testid="confirm-reschedule">
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Reagendar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report problem dialog */}
      <Dialog open={problemOpen} onOpenChange={setProblemOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reportar Problema</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Vai ser criado um Ticket de Tratamento de Pendência ligado a este contrato.</p>
            <div>
              <Label>Descrição <span className="text-rose-500">*</span></Label>
              <Textarea
                value={problemDesc}
                onChange={(e) => setProblemDesc(e.target.value)}
                rows={4}
                placeholder="Cliente recebeu fatura com valor errado, EDP cobrou 80€ em vez de 45€ acordados..."
                data-testid="problem-desc-input"
              />
              <p className="text-xs text-slate-400 mt-1">Mínimo 10 caracteres ({problemDesc.trim().length}/10)</p>
            </div>
            <div>
              <Label>Prioridade</Label>
              <Select value={problemPrio} onValueChange={setProblemPrio}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baixa">Baixa</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProblemOpen(false)} disabled={acting}>Cancelar</Button>
            <Button onClick={handleReportProblem} disabled={acting || problemDesc.trim().length < 10} className="bg-rose-600 hover:bg-rose-700 gap-2" data-testid="confirm-report-problem">
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <TicketIcon className="w-4 h-4" />} Criar Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default FollowUpsFaturaPage;
